import "../../css/ProfilePage/ProfilePage.css";
import { memo, useCallback, useMemo, useState } from "react";
import {
  AccountData,
  Follower,
  FollowerMembershipState,
  MembershipPromoCode,
  OnboardingFlowType,
} from "@markit/common.types";
import { isDesktop as isDesktopDevice } from "react-device-detect";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import {
  checkUserHasSocials,
  handleLinkPressed,
  hasSubscription,
} from "@markit/common.utils";
import ProfilePageEditProfile from "./ProfilePageEditProfile";
import AlertContainer from "../Containers/AlertContainer";
import RectangleButton from "../Buttons/RectangleButton";
import ShareProfileLinkModal from "../CreatorDashboard/CreatorProfile/ShareProfileLinkModal";
import ProfileSettingsModal from "../CreatorDashboard/CreatorProfile/ProfileSettingsModal";
import { onProfilePageNavigatePath } from "../../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import { ProfileFollowingActionsButton } from "../Buttons/ProfileFollowingActionsButton";
import Skeleton from "react-loading-skeleton";
import PaymentFailedButton from "../Subscription/PaymentFailedButton";
import MembershipPaymentFailedButtonModal from "../Subscription/Memberships/MembershipPaymentFailedButtonModal";
import GroupPlanOwnerShare from "./SubscribeFlow/GroupPlanOwnerShare";
import { getUserMembershipPromoCodeData } from "../../utils/FirebaseUtils";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import {
  checkIfFollowerIsSubscribed,
  isCreatorBasedAccount,
} from "../../utils/userUtils";
import ProfileExpandedItem from "../DisplayItem/ProfileItem/ProfileExpandedItem";
import OnboardingTourStep from "../Onboarding/OnboardingTourStep";
import { PROFILE_PICTURE_ONBOARDING_STEPS } from "../../utils/onboardingFlowUtils";

export enum SocialMediaType {
  INSTAGRAM = "instagram",
  LINKEDIN = "linkedin",
  TWITTER = "twitter",
  TIKTOK = "tiktok",
  SPOTIFY = "spotify",
  YOUTUBE = "youtube",
}

type ProfilePageHeaderProps = {
  followerData: Follower | undefined;
  otherUserData?: AccountData;
  isLoading: boolean;
  forceMobile?: boolean;
};

const ProfilePageHeader = memo(function ProfilePageHeaderFn({
  followerData,
  otherUserData,
  isLoading,
  forceMobile,
}: ProfilePageHeaderProps) {
  const { account } = useSelector(getAccountState);
  const { accountData, loggedIn, teamMemberData } = account;
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [shareModal, setShareModal] = useState(false);
  const [paymentFailedModal, setPaymentFailedModal] = useState(false);
  const [groupMembership, setGroupMembership] = useState<MembershipPromoCode>();

  const styles = {
    container: {
      paddingInline: 14,
    },
    headerButton: {
      padding: "14px 40px",
      flex: 1,
      whiteSpace: "nowrap",
    },
    settingsContainer: {
      cursor: "pointer",
      width: 45,
      height: 45,
      borderRadius: 12,
      border: `0.5px solid ${Colors.GRAY2}`,
      backgroundColor: Colors.WHITE1,
    },
  };

  const isDesktop = useMemo(
    () => isDesktopDevice && !forceMobile,
    [forceMobile]
  );

  const userData = useMemo(
    () => otherUserData || accountData,
    [accountData, otherUserData]
  );

  const isMyAccount = useMemo(
    () =>
      loggedIn === LoginState.LOGGED_IN && accountData.uid === userData?.uid,
    [accountData.uid, loggedIn, userData?.uid]
  );

  const onShareProfile = useCallback(() => {
    setShareModal(true);
  }, []);

  const navigateSubscribe = useCallback(
    () => onProfilePageNavigatePath(navigate, userData.username, "subscribe"),
    [navigate, userData.username]
  );

  const renderSocialMedia = useCallback(
    (type: SocialMediaType, link: string, iconName: string) => {
      return link ? (
        <div
          className="ProfileBrowserSocialMediaIcon"
          style={{ marginBottom: -3 }}
          onClick={() => window.open(handleLinkPressed(type, link), "_blank")}
        >
          <Icon icon={iconName} height={16} style={{ color: Colors.GRAY3 }} />
        </div>
      ) : null;
    },
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    // determine if the subscriber is a group plan owner
    if (followerData?.groupOwnerPromoCodeId) {
      const membershipPromoCode = await getUserMembershipPromoCodeData(
        userData.uid,
        followerData.groupOwnerPromoCodeId
      );
      setGroupMembership(membershipPromoCode);
    }
  }, [followerData?.groupOwnerPromoCodeId, userData.uid]);

  return (
    <>
      <div style={{ width: isDesktop ? 330 : "100%" }}>
        <div
          className="ColumnNormal"
          style={{ gap: 14, paddingInline: isDesktopDevice ? 0 : 14 }}
        >
          <div className="ColumnCentering" style={{ gap: isDesktop ? 14 : 10 }}>
            <div className="onboarding-profile-pic-photo-icon-target">
              <ProfileExpandedItem
                user={userData}
                size={isDesktop ? 100 : 80}
              />
            </div>
            {!isEditing &&
            isMyAccount &&
            !forceMobile &&
            !accountData.profilePicURL &&
            !teamMemberData ? (
              <OnboardingTourStep
                key="profile-pic-tour-step-1"
                flowType={OnboardingFlowType.PROFILE_PIC}
                target=".onboarding-profile-pic-photo-icon-target"
                content={
                  <>
                    Add a profile picture so people recognize you. It shows up
                    on your link-in-bio page as well as your contact card when
                    they receive texts from you.
                    {!hasSubscription(accountData) ? (
                      <>
                        <br />
                        <br />
                        You&apos;ll need Markit+ to text from a personal number
                        so users can receive your contact card.
                      </>
                    ) : null}
                    <br />
                    <br />
                    Click &quot;Edit Profile&quot; to add yours.
                  </>
                }
                stepNumber={1}
                totalSteps={PROFILE_PICTURE_ONBOARDING_STEPS}
                placement="right"
              />
            ) : null}
            <span
              style={{ fontSize: 20, fontWeight: 500, textAlign: "center" }}
            >
              {userData.fullName}
            </span>
            {userData.bio || checkUserHasSocials(userData) ? (
              <div className="ColumnCentering" style={{ gap: 10 }}>
                {userData.bio ? (
                  <span
                    style={{
                      fontSize: 14,
                      textAlign: "center",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {userData.bio}
                  </span>
                ) : null}
                {checkUserHasSocials(userData) ? (
                  <div className="AlignedRow" style={{ gap: 8 }}>
                    {renderSocialMedia(
                      SocialMediaType.INSTAGRAM,
                      userData.instagram,
                      "mdi:instagram"
                    )}
                    {renderSocialMedia(
                      SocialMediaType.TWITTER,
                      userData.twitter,
                      "ri:twitter-x-fill"
                    )}
                    {renderSocialMedia(
                      SocialMediaType.LINKEDIN,
                      userData.linkedin,
                      "ri:linkedin-fill"
                    )}
                    {renderSocialMedia(
                      SocialMediaType.TIKTOK,
                      userData.tiktok,
                      "ic:baseline-tiktok"
                    )}
                    {renderSocialMedia(
                      SocialMediaType.SPOTIFY,
                      userData.spotify,
                      "mdi:spotify"
                    )}
                    {renderSocialMedia(
                      SocialMediaType.YOUTUBE,
                      userData.youtube,
                      "ion:logo-youtube"
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {isLoading ? (
            <Skeleton height={44} baseColor={Colors.WHITE1} borderRadius={12} />
          ) : !isMyAccount || forceMobile ? (
            !followerData ||
            !checkIfFollowerIsSubscribed(followerData.status) ? (
              <RectangleButton
                buttonLabel="Subscribe"
                onPress={navigateSubscribe}
                altColor={Colors.BLACK}
                containerStyles={{
                  ...styles.headerButton,
                  pointerEvents: forceMobile ? "none" : "auto",
                }}
              />
            ) : (
              <div className="ColumnNormal" style={{ gap: 20 }}>
                <div className="AlignedRow" style={{ gap: 10 }}>
                  {otherUserData ? (
                    <div style={{ flex: 1 }}>
                      <ProfileFollowingActionsButton
                        followerData={followerData}
                        creatorData={otherUserData}
                      />
                    </div>
                  ) : null}
                  <div
                    className="Centering"
                    style={styles.settingsContainer}
                    onClick={onShareProfile}
                  >
                    <Icon
                      icon="ion:share-outline"
                      height={18}
                      style={{ color: Colors.BLACK }}
                    />
                  </div>
                </div>
                {followerData &&
                followerData.membershipState ===
                  FollowerMembershipState.PAYMENT_FAILED ? (
                  <PaymentFailedButton
                    type="Subscriber"
                    onPress={() => setPaymentFailedModal(true)}
                  />
                ) : null}
              </div>
            )
          ) : (
            <div className="AlignedRow" style={{ gap: 8, flex: 1 }}>
              {!accountData.inCreatorMode ? (
                <RectangleButton
                  buttonLabel="Share"
                  onPress={onShareProfile}
                  altColor={Colors.BLACK}
                  containerStyles={{
                    ...styles.headerButton,
                    padding: "14px 20px",
                  }}
                />
              ) : null}
              {!teamMemberData ? (
                <>
                  <div style={{ flex: 1 }}>
                    <RectangleButton
                      buttonLabel="Edit Profile"
                      iconRight={
                        <Icon
                          icon="ion:create"
                          height={14}
                          style={{ color: Colors.BLACK, marginTop: -1 }}
                        />
                      }
                      onPress={() => setIsEditing(true)}
                      altColor={Colors.WHITE1}
                      altTextColor={Colors.BLACK}
                      containerStyles={{
                        ...styles.headerButton,
                        padding: "14px 20px",
                        border: `0.5px solid ${Colors.GRAY2}`,
                      }}
                    />
                  </div>
                  {isCreatorBasedAccount(accountData) ? (
                    <ProfileSettingsModal />
                  ) : null}
                </>
              ) : null}
            </div>
          )}
          {groupMembership && followerData ? (
            <GroupPlanOwnerShare
              creatorData={userData}
              membershipPromoCode={groupMembership}
              followerData={followerData}
            />
          ) : null}
        </div>
      </div>
      <AlertContainer
        headerComp={alertText}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
      {isEditing ? (
        <ProfilePageEditProfile
          isVisible={isEditing}
          setIsVisible={setIsEditing}
        />
      ) : null}
      {shareModal ? (
        <ShareProfileLinkModal
          username={userData.username}
          setShareLinkModal={setShareModal}
        />
      ) : null}
      {paymentFailedModal && followerData ? (
        <MembershipPaymentFailedButtonModal
          setIsVisible={setPaymentFailedModal}
          followerDataMap={new Map([[userData.uid, followerData]])}
          followingAccountDataFollowing={[userData]}
        />
      ) : null}
    </>
  );
});

export default ProfilePageHeader;
